/**
 * Act 1, beat 1 — ***Born***.
 *
 * Spec §7: *"Farrowing crate. Tutorial by curiosity: nudge, root, look. Her
 * mother cannot turn around; the bars are the first thing the player
 * understands. A sibling stops moving. Implied entirely by a worker's boots, a
 * curtain drawn, and an empty space next to her."*
 *
 * ── The rule this beat is written under ──────────────────────────────────────
 *
 * **The player does not see it happen.** The boots stop at the pen, the heat
 * lamp's light is blocked by somebody leaning over, and when the light comes
 * back the littermate is not there and the straw where it lay is flat. No hand,
 * no lift, no sound of anything but boots and a door. Spec §2's content
 * restraint asks for horror delivered by "silhouette, sound, absence, and
 * implication", and this is the whole of it: an absence, and the two seconds of
 * shadow that the player will fill in themselves for the rest of the game.
 *
 * Everything about that is cheaper than showing it, and it is also better. There
 * is no worker model above the knee (`worker-legs.ts` explains why), no animated
 * carry, no cutaway. There is a light going out.
 *
 * ── Why the tutorial is affection and not a puzzle ───────────────────────────
 *
 * The three verbs are taught in the order **nudge → root → look**, and each is
 * learned on something she would do anyway:
 *
 *   - **nudge** on her mother, who answers. It is a greeting before it is a verb.
 *   - **nudge** again, on her sibling, who wakes, squeaks, and follows her
 *     (D-250). This is the one the boots come for, and until session 91 the
 *     player never touched it: the tutorial bonded them with her mother and
 *     then took somebody else.
 *   - **root** in the straw, which is the only pleasure available in the
 *     building, and the only thing in the beat that is simply nice.
 *   - **look** at the door, because something moved there.
 *
 * By the time the boots come back, the player has been taught to point the snout
 * at whatever they care about — which is exactly the reflex the last stage
 * needs, when the thing they want to point it at is a patch of empty straw.
 * The sibling answered that nudge once; the straw where it lay does not.
 *
 * ── Nothing here touches the world ───────────────────────────────────────────
 *
 * Stages emit **cues**; `main.ts` decides what a cue means. That is the seam
 * `src/story/` exists for, and it is why this beat replays in Node with no
 * renderer. If a stage in this file ever imports Three, the beat has stopped
 * being testable and the mistake will be here rather than in the sequencer.
 */

import type { Beat, Stage, StoryContext } from '@/story/types';

/**
 * Interactable ids, shared between this beat and the world that places them.
 *
 * Exported as one object rather than as loose strings because a typo in an id is
 * silent in both directions — the stage waits for a completion that never
 * arrives, and the interactable is never waited on by anything.
 */
export const BORN_IDS = {
  /** Her mother's flank, through the bars. Nudge only; it never completes. */
  mother: 'born.mother',
  /** The littermate who will be taken, awake or asleep. Nudge wakes it (D-250). */
  sibling: 'born.sibling',
  /** The straw bed. Hold to root. */
  straw: 'born.straw',
  /** The doorway, watched from across the pen. Hold to observe. */
  door: 'born.door',
  /** The flattened patch where her littermate was. Nudge; nothing answers. */
  empty: 'born.empty',
} as const;

/**
 * Story flags the GAME raises, as opposed to the ones the interaction system
 * completes on its own.
 *
 * There is exactly one, and it exists because `nudge` has no duration and
 * therefore never "completes" — which is right, since nudging is not a task. The
 * game counts them and raises this once her mother has answered.
 */
export const BORN_FLAGS = {
  motherAnswered: 'born.motherAnswered',
  /** Her sibling woke to her nudge (D-250). Same reason: `nudge` never completes. */
  siblingWoken: 'born.siblingWoken',
} as const;

/** Every cue this beat can emit. `main.ts` switches on these and nothing else. */
export const BORN_CUES = {
  /** Black screen, sound only. */
  wake: 'born.wake',
  /** The slow pull-back that reveals the crate and her mother in it. */
  reveal: 'born.reveal',
  /** Camera released to the player; her verbs come alive. */
  handOver: 'born.handOver',
  /** Her sibling can be woken now (D-250). */
  offerSibling: 'born.offerSibling',
  /** The straw is worth rooting in now. */
  offerStraw: 'born.offerStraw',
  /** Something moves at the door, and it can be watched. */
  offerDoor: 'born.offerDoor',
  /** Boots through the walkway door, walking up the hall. */
  bootsEnter: 'born.bootsEnter',
  /** Boots stopped at her pen. */
  bootsArrive: 'born.bootsArrive',
  /** He has bent into the pen through its open gate. The lamp goes out. */
  lampBlocked: 'born.lampBlocked',
  /** The light comes back, and the straw beside her is flat and empty. */
  taken: 'born.taken',
  /** Boots back down the hall and out through the walkway door. */
  bootsLeave: 'born.bootsLeave',
  /** Her mother stops answering. */
  sowSettle: 'born.sowSettle',
  /** Fade to black. */
  fadeOut: 'born.fadeOut',
  /** The beat is over. */
  end: 'born.end',
} as const;

/**
 * How long the player may dawdle in a tutorial stage before it moves on anyway.
 *
 * Spec §10 forbids softlocks, and "the player never worked out that E does
 * something" is a softlock with better manners. Generous rather than tight: the
 * beat is three minutes long and a player who is looking around rather than
 * pressing buttons is having the intended experience, not failing.
 *
 * When a stage times out the verb is NOT taken away — the interactable stays in
 * the world, and it can still be used afterwards. The beat simply stops waiting.
 */
const PATIENCE = 45;

/** A stage that waits for a flag, or for the player to have had long enough. */
function waitFor(id: string, flag: string, enterCue?: string): Stage {
  return {
    id,
    ...(enterCue === undefined ? {} : { enter: (_ctx, emit): void => emit(enterCue) }),
    done: (ctx: StoryContext): boolean => ctx.completed.has(flag) || ctx.stageTime >= PATIENCE,
  };
}

/** A stage that is purely a length of time with a cue at the front of it. */
function hold(id: string, seconds: number, ...cues: readonly string[]): Stage {
  return {
    id,
    enter: (_ctx, emit): void => {
      for (const cue of cues) emit(cue);
    },
    done: (ctx: StoryContext): boolean => ctx.stageTime >= seconds,
  };
}

export const BORN: Beat = {
  number: 1,
  id: 'act1.born',
  stages: [
    // ── the wake ─────────────────────────────────────────────────────────────
    // Black, and the hall. The first thing in this game is a sound, and what the
    // player hears before they see anything is machinery — spec §8 puts no score
    // inside the farm, so the building introduces itself in its own voice.
    hold('dark', 2.2, BORN_CUES.wake),

    // The pull-back. It ends on the crate, from underneath, which is the only
    // angle from which its bars and her mother's size are the same fact.
    {
      ...hold('reveal', 6.4, BORN_CUES.reveal),
      exit: (_ctx, emit): void => emit(BORN_CUES.handOver),
    },

    // ── tutorial by curiosity ────────────────────────────────────────────────
    // Nothing is offered but her mother. There is one thing to do and one thing
    // that answers, which is as much instruction as this game will ever give.
    waitFor('nudge', BORN_FLAGS.motherAnswered),

    // Her sibling, asleep under the lamp. The same verb on somebody who can
    // get up: it wakes, squeaks, and from here to the door it goes where she
    // goes (D-250). Offered only after her mother, so the first nudge the game
    // teaches is still the one that is answered by the bars.
    waitFor('sibling', BORN_FLAGS.siblingWoken, BORN_CUES.offerSibling),

    // The straw becomes rootable only after she has greeted her mother. Order
    // matters: the straw is the fun one, and a player who finds it first has no
    // reason to go back to the bars.
    waitFor('root', BORN_IDS.straw, BORN_CUES.offerStraw),

    // Something moves at the door. This is the first time in the game that
    // anything happens somewhere she is not, and watching it is the verb. Her
    // sibling does not watch it: it goes back under the lamp and lies down,
    // which is where the boots will find it.
    waitFor('look', BORN_IDS.door, BORN_CUES.offerDoor),

    // ── the taking ───────────────────────────────────────────────────────────
    // Six stages, and the player has control through all of them. Taking the
    // camera away here would make it a cutscene, and a cutscene is a thing that
    // happens TO you — the point is that it happens beside you while you are
    // doing something else, which is how it happens.
    hold('boots-enter', 5.2, BORN_CUES.bootsEnter),
    hold('boots-arrive', 1.6, BORN_CUES.bootsArrive),
    // The two seconds the whole beat is built on.
    hold('shadow', 2.4, BORN_CUES.lampBlocked),
    hold('taken', 1.8, BORN_CUES.taken),
    hold('boots-leave', 5.6, BORN_CUES.bootsLeave),

    // ── after ────────────────────────────────────────────────────────────────
    // Her mother stops answering. Not grief — she is a sow in a crate and she
    // cannot see what happened either. She simply goes quiet, and the player is
    // left with the only two things in the pen that have changed: a flat patch
    // of straw, and nobody in it.
    {
      id: 'empty',
      enter: (_ctx, emit): void => {
        emit(BORN_CUES.sowSettle);
      },
      // Long, and it does not require the player to do anything. If they put
      // their snout in the empty straw, the beat notices and moves on sooner —
      // but a player who just stands there gets the same ending, because that is
      // also a legitimate response to it.
      done: (ctx: StoryContext): boolean =>
        ctx.completed.has(BORN_IDS.empty) ? ctx.stageTime >= 4 : ctx.stageTime >= 22,
    },

    {
      ...hold('close', 5, BORN_CUES.fadeOut),
      exit: (_ctx, emit): void => emit(BORN_CUES.end),
    },
  ],
};
